import { ArrowRight, ChevronDown } from "lucide-react";
import TechChip from "../components/content/TechChip";
import Accordion from "../components/sections/Accordion";
import CtaPanel from "../components/sections/CtaPanel";
import PageHero from "../components/sections/PageHero";
import { ButtonLink } from "../components/ui/Button";
import SectionHeading from "../components/ui/SectionHeading";
import { images } from "../data/images";
import {
  buildKinds,
  faqs,
  headaches,
  pathSteps,
  servicesPage as copy,
} from "../data/services";
import { stack } from "../data/tech";
import { localHref } from "../lib/links";

/** One headache: the symptoms, what we do about it, and a real result. */
function HeadacheCard({ tag, title, symptoms, fix, proof }) {
  return (
    <li className="flex flex-col overflow-hidden rounded-brand border border-line bg-white shadow-soft">
      <div className="bg-slate px-6 pt-6 pb-5 text-white">
        <span className="tag text-[11px] text-yellow">{tag}</span>
        <h3 className="font-display mt-2 text-[24px] leading-[1.1] uppercase">
          {title}
        </h3>
        <ul className="mt-4">
          {symptoms.map((symptom) => (
            <li
              key={symptom}
              className="relative border-t border-white/12 py-2.5 pl-7 text-[15px] text-white/85"
            >
              <span
                aria-hidden="true"
                className="absolute top-2.5 left-0.5 font-bold text-yellow"
              >
                ✕
              </span>
              {symptom}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex flex-1 flex-col px-6 pt-5 pb-6">
        <p className="text-[15.5px] text-ink">
          <strong className="tag mr-1.5 text-[11px] text-blue-deep">
            The fix
          </strong>
          {fix}
        </p>
        <a
          href={localHref(proof.href)}
          className="group mt-5 flex items-end justify-between gap-4 border-t border-line pt-4"
        >
          <span>
            <span className="font-display block text-[34px] leading-none text-slate">
              {proof.value}
            </span>
            <span className="mt-1.5 block text-[14px] text-muted">
              <strong className="text-ink">{proof.company}:</strong>{" "}
              {proof.label}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1 text-sm font-bold text-blue-deep group-hover:underline">
            Story
            <ArrowRight size={15} aria-hidden="true" />
            <span className="sr-only">: {proof.company}</span>
          </span>
        </a>
      </div>
    </li>
  );
}

function WhatWeFix() {
  return (
    <section id="headaches" className="bg-surface px-4 py-20 sm:px-6 md:py-24">
      <div className="mx-auto max-w-280">
        <SectionHeading
          eyebrow={copy.fixEyebrow}
          title={copy.fixTitle}
          className="mb-11"
        >
          <p>{copy.fixText}</p>
        </SectionHeading>
        <ul className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {headaches.map((headache) => (
            <HeadacheCard key={headache.tag} {...headache} />
          ))}
          <li className="flex flex-col justify-between rounded-brand bg-yellow p-7 text-ink">
            <div>
              <span className="tag text-yellow-ink">Start here</span>
              <h3 className="font-display mt-3 mb-3 text-[28px] leading-[1.1] uppercase">
                {copy.moreTitle}
              </h3>
              <p className="text-[15.5px] font-bold text-ink/80">
                {copy.moreText}
              </p>
            </div>
            <ButtonLink variant="dark" className="mt-7 self-start" />
          </li>
        </ul>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section
      id="technology-solution-path"
      className="blueprint px-4 py-20 text-white sm:px-6 md:py-24"
    >
      <div className="mx-auto max-w-280">
        <SectionHeading dark eyebrow={copy.pathEyebrow} title={copy.pathTitle}>
          <p>{copy.pathText}</p>
        </SectionHeading>
        <ol className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {pathSteps.map(({ title, text }, i) => (
            <li
              key={title}
              className="rounded-brand border border-white/12 bg-white/4 p-6"
            >
              <div className="mb-4 flex items-center gap-4">
                <span className="font-display text-[44px] leading-none text-yellow">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="h-px flex-1 bg-linear-to-r from-yellow/60 to-transparent"
                />
              </div>
              <h3 className="font-display mb-2 text-[22px] uppercase">
                {title}
              </h3>
              <p className="text-[15.5px] text-white/75">{text}</p>
            </li>
          ))}
        </ol>
        <ButtonLink className="mt-10" />
      </div>
    </section>
  );
}

/** The technology, kept short: plain-language kinds, toolbox on request. */
function TheTech() {
  return (
    <section className="bg-white px-4 py-20 sm:px-6 md:py-24">
      <div className="mx-auto max-w-280">
        <SectionHeading eyebrow={copy.techEyebrow} title={copy.techTitle}>
          <p>{copy.techText}</p>
        </SectionHeading>
        <ul className="mt-10 grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-5">
          {buildKinds.map(({ title, text, icon: Icon }) => (
            <li key={title} className="flex gap-3.5 lg:flex-col lg:gap-3">
              <Icon
                size={24}
                strokeWidth={1.75}
                className="mt-0.5 shrink-0 text-blue-deep"
                aria-hidden="true"
              />
              <div>
                <h3 className="text-[16px] font-bold text-slate">{title}</h3>
                <p className="mt-1 text-[15px] text-muted">{text}</p>
              </div>
            </li>
          ))}
        </ul>
        <details className="group mt-10 border-t border-line pt-6">
          <summary className="inline-flex cursor-pointer list-none items-center gap-2 font-bold text-blue-deep hover:underline [&::-webkit-details-marker]:hidden">
            {copy.techToggle}
            <ChevronDown
              size={18}
              aria-hidden="true"
              className="transition-transform group-open:rotate-180"
            />
          </summary>
          <ul
            aria-label="Technologies we build with"
            className="mt-6 flex flex-wrap gap-3"
          >
            {stack.map((tech) => (
              <li key={tech.name}>
                <TechChip tech={tech} />
              </li>
            ))}
          </ul>
        </details>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="bg-white px-4 pb-20 sm:px-6 md:pb-24">
      <div className="mx-auto grid max-w-280 grid-cols-1 items-start gap-10 pt-4 lg:grid-cols-[400px_minmax(0,1fr)] lg:gap-16">
        <div className="lg:sticky lg:top-28">
          <SectionHeading eyebrow={copy.faqEyebrow} title={copy.faqTitle} />
        </div>
        <Accordion items={faqs} />
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <>
      <PageHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        lede={copy.lede}
        image={images.someroScreed}
        imageAlt="A Somero laser screed placing concrete on a job site"
      >
        <div className="flex flex-wrap gap-3.5">
          <ButtonLink />
          <ButtonLink href="#headaches" variant="glass">
            {copy.secondary} ↓
          </ButtonLink>
        </div>
      </PageHero>
      <WhatWeFix />
      <HowItWorks />
      <TheTech />
      <Faq />
      <CtaPanel />
    </>
  );
}
